import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Music, Share2, AlertCircle, Mail } from "lucide-react";

const LABELS = {
  spotify: "Spotify",
  soundcloud: "SoundCloud",
  instagram: "Instagram",
  facebook: "Facebook",
  youtube: "YouTube",
};

// Map a connection "platform" to the OAuth provider that owns it.
const PROVIDER_OF = {
  spotify: "spotify",
  instagram: "meta",
  facebook: "meta",
  youtube: "youtube",
  soundcloud: "soundcloud",
};

export default function Connections() {
  const [conns, setConns] = useState([]);
  const [gmail, setGmail] = useState(null);
  const [providerStatus, setProviderStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const loadAll = async () => {
    const [c, s, m, y, sc] = await Promise.all([
      api.get("/connections"),
      api.get("/oauth/spotify/status"),
      api.get("/oauth/meta/status"),
      api.get("/oauth/youtube/status"),
      api.get("/oauth/soundcloud/status"),
    ]);
    setConns(c.data);
    api.get("/ade/gmail/status").then((g) => setGmail(g.data)).catch(() => setGmail({ configured: false, connected: false }));
    setProviderStatus({
      spotify: s.data.configured,
      meta: m.data.configured,
      youtube: y.data.configured,
      soundcloud: sc.data.configured,
    });
  };

  useEffect(() => {
    loadAll().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const oauth = params.get("oauth");
    const prov = params.get("provider");
    if (oauth === "success") {
      toast.success(`Connected ${prov}.`);
      setParams({});
      loadAll();
    } else if (oauth === "error") {
      toast.error(`Failed to connect ${prov}.`);
      setParams({});
    }
  }, [params, setParams]);

  const connect = async (platform) => {
    const provider = PROVIDER_OF[platform];
    if (!provider) {
      toast(`${LABELS[platform]} OAuth isn't wired yet — connecting demo.`);
      await api.post(`/connections/${platform}/toggle`);
      loadAll();
      return;
    }
    if (!providerStatus[provider]) {
      toast.error(
        `${LABELS[platform]} needs credentials first. See /app/SETUP_SOCIAL_APIS.md.`,
        { duration: 6000 }
      );
      return;
    }
    try {
      const res = await api.get(`/oauth/${provider}/start?return_to=/connections`);
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start OAuth");
    }
  };

  const disconnect = async (platform) => {
    await api.post(`/connections/${platform}/toggle`);
    loadAll();
    toast(`${LABELS[platform]} disconnected`);
  };

  const connectGmail = async () => {
    try {
      const res = await api.get(`/oauth/gmail/start?return_to=/connections`);
      window.location.href = res.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Gmail OAuth");
    }
  };
  const disconnectGmail = async () => {
    await api.post("/ade/gmail/disconnect");
    loadAll();
    toast("Gmail disconnected");
  };

  const music = conns.filter((c) => c.kind === "music");
  const social = conns.filter((c) => c.kind === "social");

  const unconfigured = Object.entries(providerStatus).filter(([, v]) => !v).map(([k]) => k);

  return (
    <div className="fade-in" data-testid="connections-page">
      <div className="mb-10">
        <div className="label-mono mb-4">Integrations</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
          Connect the pipes.
        </h1>
        <p className="text-white/55 mt-4 max-w-xl">
          Music sources feed releases into the agent. Social destinations receive approved posts.
        </p>
        {unconfigured.length > 0 && (
          <div className="panel p-5 mt-6 flex items-start gap-4 border-[#FFD600]/40" data-testid="oauth-setup-banner">
            <AlertCircle size={18} className="text-[#FFD600] shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="text-white font-medium">
                OAuth credentials needed for: {unconfigured.join(", ").toUpperCase()}
              </div>
              <div className="text-white/55 mt-1">
                Open <span className="font-mono text-white/80">/app/SETUP_SOCIAL_APIS.md</span> for step-by-step instructions.
                Unconfigured providers still expose a "Connect (demo)" fallback.
              </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="label-mono">Loading…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-10">
          <Section title="Music sources" Icon={Music} items={music} onConnect={connect} onDisconnect={disconnect} providerStatus={providerStatus} testidPrefix="music" />
          <Section title="Social destinations" Icon={Share2} items={social} onConnect={connect} onDisconnect={disconnect} providerStatus={providerStatus} testidPrefix="social" />
          <section className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
              <Mail size={16} className="text-white/50" strokeWidth={1.5} />
              <h2 className="font-display text-2xl tracking-tight">Outreach mailbox</h2>
            </div>
            <ul className="space-y-px bg-white/[0.06]">
              <li className="bg-[#030303] p-5 flex items-center justify-between gap-4" data-testid="conn-gmail">
                <div className="min-w-0">
                  <div className="font-sans text-base">Gmail · ADE Outreach</div>
                  <div className="label-mono mt-1 truncate">
                    {gmail?.connected
                      ? `${gmail.email || "connected"} · drafts sent as ${gmail.sender_email}`
                      : (gmail?.configured ? "not connected · drafts, reply tracking, calendar, 8am digest" : "not connected · Google OAuth not configured")}
                  </div>
                </div>
                {gmail?.connected ? (
                  <button className="btn-ghost-small" onClick={disconnectGmail} data-testid="mail-toggle-gmail">Disconnect</button>
                ) : (
                  <button className="btn-amber-small" onClick={connectGmail} disabled={!gmail?.configured} data-testid="mail-toggle-gmail">Connect</button>
                )}
              </li>
            </ul>
            {gmail && !gmail.connected && gmail.redirect_uri && (
              <div className="label-mono mt-3 text-white/45 break-all" data-testid="gmail-redirect-uri">
                Google Cloud → OAuth client → Authorized redirect URIs must contain exactly:{" "}
                <button
                  type="button"
                  className="text-white/80 underline underline-offset-2"
                  onClick={() => { navigator.clipboard?.writeText(gmail.redirect_uri); toast("Redirect URI copied"); }}
                  title="Click to copy"
                >
                  {gmail.redirect_uri}
                </button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Section({ title, Icon, items, onConnect, onDisconnect, providerStatus, testidPrefix }) {
  return (
    <section>
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
        <Icon size={16} className="text-white/50" strokeWidth={1.5} />
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      </div>
      <ul className="space-y-px bg-white/[0.06]">
        {items.map((c) => {
          const provider = PROVIDER_OF[c.platform];
          const isDemo = !provider || !providerStatus[provider];
          return (
            <li
              key={c.platform}
              className="bg-[#030303] p-5 flex items-center justify-between"
              data-testid={`conn-${c.platform}`}
            >
              <div>
                <div className="font-sans text-base">{LABELS[c.platform]}</div>
                <div className="label-mono mt-1">
                  {c.connected ? (c.handle || "connected") : (isDemo ? "not connected · demo" : "not connected")}
                </div>
              </div>
              {c.connected ? (
                <button
                  className="btn-ghost-small"
                  onClick={() => onDisconnect(c.platform)}
                  data-testid={`${testidPrefix}-toggle-${c.platform}`}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  className="btn-amber-small"
                  onClick={() => onConnect(c.platform)}
                  data-testid={`${testidPrefix}-toggle-${c.platform}`}
                >
                  {isDemo ? "Connect (demo)" : "Connect"}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
